/* ============================================================
   androidCalibration.js — "Peta telefon" per-telefon (kalibrasi)
   ============================================================
   Setiap telefon beza → kedudukan butang pada skrin bergerak beza. Masa
   larian Test, bila AI (vision) jumpa butang, kita RAKAM koordinatnya →
   lain kali guna terus (laju, tak perlu AI). Itulah yang buat "robot tiap
   pelanggan berbeza" dan pelanggan TAK perlu tunggu admin.

   SIMPAN DI MANA: localStorage mesin yang post. Sebab telefon MEMANG dicucuk
   ke PC itu (WebUSB) → peta duduk betul di tempat ia dipakai. Tiada tulisan
   ke Firestore (tiada rules deploy, tiada risiko 1 MB doc user). Kunci ikut
   appId + serial telefon → satu telefon satu peta.

   ⛔ Semua baca/tulis dibalut try/catch (storan boleh ditutup / private mode)
   dan MESTI jalan elok walau storan kosong. Peta rosak ≠ bencana: runner
   jatuh balik ke selector/vision.
   ============================================================ */

const CALIB_KEY = 'sesaat_phone_calib_v1';

function loadAll() {
  try {
    const raw = localStorage.getItem(CALIB_KEY);
    const obj = raw ? JSON.parse(raw) : {};
    return obj && typeof obj === 'object' ? obj : {};
  } catch { return {}; }
}

function saveAll(obj) {
  try { localStorage.setItem(CALIB_KEY, JSON.stringify(obj)); return true; } catch { return false; }
}

function keyOf(appId, serial) {
  return `${String(appId || '').trim()}__${String(serial || '').trim()}`;
}

// Peta satu telefon: { coords:{stepId:{x,y}}, model, ready:bool, savedAt }.
export function loadCalib(appId, serial) {
  const all = loadAll();
  const c = all[keyOf(appId, serial)];
  return c && typeof c === 'object' ? c : { coords: {}, ready: false };
}

// Koordinat tersimpan untuk satu langkah (null kalau tiada). Disemak: nombor
// terhingga, dalam julat skrin kalau diberi (elak koordinat rosak → tap merata).
export function getCoord(appId, serial, stepId, sw, sh) {
  const c = loadCalib(appId, serial).coords || {};
  const p = c[stepId];
  if (!p) return null;
  const x = Number(p.x), y = Number(p.y);
  // x/y 0 = sampah (vision tiada sasaran sebenar; 31 Okt peta ai_label tersimpan 0,0).
  if (!Number.isFinite(x) || !Number.isFinite(y) || x <= 0 || y <= 0) return null;
  if (sw && x > sw + 2) return null;
  if (sh && y > sh + 2) return null;
  return { x, y };
}

// Rakam koordinat satu langkah (selepas vision berjaya). Semak nilai dulu.
export function saveCoord(appId, serial, stepId, x, y, meta = {}) {
  const nx = Number(x), ny = Number(y);
  if (!Number.isFinite(nx) || !Number.isFinite(ny) || nx <= 0 || ny <= 0) return false;
  const all = loadAll();
  const k = keyOf(appId, serial);
  const cur = all[k] && typeof all[k] === 'object' ? all[k] : { coords: {} };
  cur.coords = cur.coords && typeof cur.coords === 'object' ? cur.coords : {};
  cur.coords[stepId] = { x: Math.round(nx), y: Math.round(ny) };
  if (meta.model) cur.model = meta.model;
  cur.savedAt = Date.now();
  all[k] = cur;
  return saveAll(all);
}

// Buang koordinat satu langkah (gate gagal = kedudukan salah, jangan ulang).
export function deleteCoord(appId, serial, stepId) {
  const all = loadAll();
  const cur = all[keyOf(appId, serial)];
  if (!cur || !cur.coords || !cur.coords[stepId]) return false;
  delete cur.coords[stepId];
  return saveAll(all);
}

// Tanda telefon ni "ready" (Test lulus hujung-ke-hujung). Gate bulk run.
export function markReady(appId, serial, ready, meta = {}) {
  const all = loadAll();
  const k = keyOf(appId, serial);
  const cur = all[k] && typeof all[k] === 'object' ? all[k] : { coords: {} };
  cur.ready = !!ready;
  if (meta.model) cur.model = meta.model;
  cur.readyAt = ready ? Date.now() : null;
  all[k] = cur;
  return saveAll(all);
}

// version diberi → ready hanya sah untuk versi resipi semasa (peta basi = belum ready).
export function isReady(appId, serial, version) {
  const c = loadCalib(appId, serial);
  if (version != null && c.recipeVersion !== version) return false;
  return !!c.ready;
}

// Telefon terbaru yang lulus Test pada browser ini, untuk paparan Control Panel (telefon
// mungkin belum disambung, jadi serial tak diketahui). null = belum ada. Run tetap semak
// telefon sebenar melalui isReady.
export function telefonSedia(appId, version) {
  const awalan = keyOf(appId, '');
  const senarai = Object.entries(loadAll())
    .filter(([k, c]) => k.startsWith(awalan) && c?.ready && (version == null || c.recipeVersion === version))
    .map(([, c]) => c)
    .sort((a, b) => (b.readyAt || 0) - (a.readyAt || 0));
  return senarai[0] ? { model: senarai[0].model || '' } : null;
}

// Pastikan peta sepadan dengan versi resipi semasa. Versi berbeza (resipi
// diubah) → buang peta lama + cap versi baharu supaya robot belajar semula.
// Pulang true kalau ada data lama dibuang. Dipanggil di awal setiap larian.
export function ensureVersion(appId, serial, version) {
  const all = loadAll();
  const k = keyOf(appId, serial);
  const cur = all[k] && typeof all[k] === 'object' ? all[k] : null;
  if (cur && cur.recipeVersion === version) return false; // sepadan → kekalkan peta
  all[k] = { coords: {}, recipeVersion: version }; // basi/baharu → reset bersih + cap versi
  saveAll(all);
  return !!cur;
}

// Buang peta satu telefon (kalau pelanggan nak kalibrasi semula).
export function clearCalib(appId, serial) {
  const all = loadAll();
  delete all[keyOf(appId, serial)];
  return saveAll(all);
}
